import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

// Base Modal Component
export const Modal = ({ isOpen, onClose, title, children, maxWidth = 'max-w-md' }) => {
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={`w-full ${maxWidth} bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl shadow-2xl`}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-[#4A0A66] flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">{title}</h3>
          <button 
            onClick={onClose} 
            className="p-2 hover:bg-[#4A0A66]/30 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-[#FDF2F8]/50 hover:text-white" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6">
          {children}
        </div>
      </div>
    </div>
  );
};

// Confirm Dialog (replaces window.confirm)
export const ConfirmDialog = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  message, 
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmVariant = 'destructive' // 'destructive' | 'default' | 'success'
}) => {
  const variants = {
    destructive: 'bg-red-600 hover:bg-red-700',
    default: 'bg-gradient-to-r from-[#df1283] to-[#FF7901]',
    success: 'bg-[#22c55e] hover:bg-[#16a34a]'
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <div className="space-y-6">
        <p className="text-[#FDF2F8]/80">{message}</p>
        <div className="flex justify-end gap-3">
          <Button 
            variant="outline" 
            onClick={onClose}
            className="border-[#4A0A66] text-[#FDF2F8]/70"
          >
            {cancelText}
          </Button>
          <Button 
            onClick={() => { onConfirm(); onClose(); }}
            className={variants[confirmVariant]}
          >
            {confirmText}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// Prompt Dialog (replaces window.prompt)
export const PromptDialog = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  title, 
  message,
  placeholder = '',
  defaultValue = '',
  submitText = 'Submit',
  cancelText = 'Cancel',
  inputType = 'text'
}) => {
  const [value, setValue] = useState(defaultValue);

  useEffect(() => {
    if (isOpen) {
      setValue(defaultValue);
    }
  }, [isOpen, defaultValue]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (value.trim()) {
      onSubmit(value);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <form onSubmit={handleSubmit} className="space-y-6">
        {message && <p className="text-[#FDF2F8]/80">{message}</p>}
        <Input
          type={inputType}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
          autoFocus
        />
        <div className="flex justify-end gap-3">
          <Button 
            type="button"
            variant="outline" 
            onClick={onClose}
            className="border-[#4A0A66] text-[#FDF2F8]/70"
          >
            {cancelText}
          </Button>
          <Button 
            type="submit"
            className="bg-gradient-to-r from-[#df1283] to-[#FF7901]"
            disabled={!value.trim()}
          >
            {submitText}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

// Add User Dialog (specific for B2B client user invite)
export const AddUserDialog = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  clientName 
}) => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  useEffect(() => {
    if (isOpen) {
      setEmail('');
      setFirstName('');
      setLastName('');
    }
  }, [isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (email.trim()) {
      onSubmit(email, firstName, lastName);
      onClose();
    }
  };

  const isFormValid = email.trim() && firstName.trim() && lastName.trim();

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Add User to ${clientName}`} maxWidth="max-w-lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm text-[#FDF2F8]/70 mb-2">Email Address *</label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@company.com"
            className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
            autoFocus
            required
          />
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-[#FDF2F8]/70 mb-2">First Name *</label>
            <Input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="John"
              className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
              required
            />
          </div>
          <div>
            <label className="block text-sm text-[#FDF2F8]/70 mb-2">Last Name *</label>
            <Input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Doe"
              className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
              required
            />
          </div>
        </div>
        
        <p className="text-xs text-[#FDF2F8]/40">
          An invitation email will be sent to this address with instructions to join {clientName}.
        </p>
        
        <div className="flex justify-end gap-3 pt-4">
          <Button 
            type="button"
            variant="outline" 
            onClick={onClose}
            className="border-[#4A0A66] text-[#FDF2F8]/70"
          >
            Cancel
          </Button>
          <Button 
            type="submit"
            className="bg-gradient-to-r from-[#df1283] to-[#FF7901]"
            disabled={!isFormValid}
          >
            Send Invitation
          </Button>
        </div>
      </form>
    </Modal>
  );
};

// URL Prompt Dialog (for link insertion)
export const UrlPromptDialog = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  title = 'Enter URL',
  placeholder = 'https://'
}) => {
  const [url, setUrl] = useState('');

  useEffect(() => {
    if (isOpen) {
      setUrl('');
    }
  }, [isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (url.trim()) {
      onSubmit(url);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder={placeholder}
          className="bg-[#4A0A66]/30 border-[#4A0A66] text-white"
          autoFocus
        />
        <div className="flex justify-end gap-3">
          <Button 
            type="button"
            variant="outline" 
            onClick={onClose}
            className="border-[#4A0A66] text-[#FDF2F8]/70"
          >
            Cancel
          </Button>
          <Button 
            type="submit"
            className="bg-gradient-to-r from-[#df1283] to-[#FF7901]"
            disabled={!url.trim()}
          >
            Insert Link
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default Modal;
